import { getAccessToken } from '../authentication/supabaseService';
import { RevenueSourceType } from '../../types/RevenueSourceType';

/** The restaurant roles a staff member can hold — must stay the same SET as the server's
 *  `ALL_VALID_ROLES` in `server/database/staffDbService.ts` (that one is grouped by revenue
 *  access, this one by display order). This array's order IS the dropdown order:
 *  Owner → management → kitchen → bar → Cloud Counter, senior first within each group. */
export const RESTAURANT_ROLES = [
  'Owner',
  'Executive Manager', 'General Manager', 'Assistant Manager', 'Floor Manager', 'Waiter/Waitress',
  'Executive Chef', 'Chef', 'Sous Chef', 'Junior Sous Chef', 'Cook', 'Dishwasher',
  'Head Bartender', 'Bartender', 'Bar Back',
  'Cloud Counter'
];

export interface RestaurantAccess {
  allowed_section_ids: string[] | null;
  allowed_revenue_source_types: RevenueSourceType[] | null;
}

export interface FrontendStaffUser {
  id: string;
  email: string;
  name?: string;
  /** Avatar the staff member uploaded on their own profile page (S3 url). Comes
   *  along on `/users/allStaff` (the route returns the whole user row); null when
   *  they never set one. */
  profile_picture_url?: string | null;
  restaurant_ids: string[];
  restaurant_access?: Record<string, RestaurantAccess>;
  receive_price_alert_emails?: boolean;
  receive_weekly_reports_emails?: boolean;
  receive_staff_signup_emails?: boolean | null;
  permissions: string[];
  access_request?: 'pending' | 'approved' | 'rejected' | null;
  restaurant_role?: string | null;
}

export async function fetchStaffUsers(): Promise<FrontendStaffUser[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/users/allStaff', {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Failed to fetch staff: ${res.statusText}`);
  return (await res.json()) as FrontendStaffUser[];
}

/**
 * `locale` is the creator's UI language; the server sends BOTH staff-creation emails in it
 * (credentials to the new member, confirmation to the creator) and falls back to Dutch without it.
 *
 * ⚠️ Like every function in this service, the throw below carries only `res.statusText` — the
 * server's `{ error }` body is discarded. So a validation 400 reaches the modal as a generic
 * failure. Any rule the server enforces and the admin needs to READ (e.g. the 8-char password
 * floor) must therefore also be checked in the modal before calling this.
 */
export async function createStaffUser(
  email: string,
  password: string,
  restaurantRole: string,
  restaurantIds: string[],
  restaurantAccess?: Record<string, RestaurantAccess>,
  locale?: string
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/staff/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      email,
      password,
      restaurantRole,
      restaurantIds,
      restaurantAccess,
      locale
    })
  });
  if (!res.ok) throw new Error(`Failed to create staff: ${res.statusText}`);
}

export async function deleteStaffUser(staffId: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/delete`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Failed to delete staff: ${res.statusText}`);
}

export async function addRestaurantToStaff(
  staffId: string,
  restaurantId: string,
  allowedSectionIds?: string[] | null,
  allowedRevenueSourceTypes?: RevenueSourceType[] | null
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/addRestaurant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ 
      restaurantId,
      allowedSectionIds,
      allowedRevenueSourceTypes
    })
  });
  if (!res.ok) throw new Error(`Failed to add restaurant: ${res.statusText}`);
}

export async function updateStaffRestaurantAccess(
  staffId: string,
  restaurantId: string,
  allowedSectionIds: string[] | null,
  allowedRevenueSourceTypes: RevenueSourceType[] | null
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/restaurant/${restaurantId}/access`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      allowedSectionIds,
      allowedRevenueSourceTypes
    })
  });
  if (!res.ok) throw new Error(`Failed to update access: ${res.statusText}`);
}

export async function getStaffRestaurantAccess(
  staffId: string,
  restaurantId: string
): Promise<RestaurantAccess> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/restaurant/${restaurantId}/access`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Failed to fetch access: ${res.statusText}`);
  return (await res.json()) as RestaurantAccess;
}

export async function removeRestaurantFromStaff(
  staffId: string,
  restaurantId: string
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/deleteRestaurant/${restaurantId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Failed to remove restaurant: ${res.statusText}`);
}

export async function updateStaffPermissions(
  staffId: string,
  permissions: string[]
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/permissions`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ permissions })
  });
  if (!res.ok) throw new Error(`Failed to update permissions: ${res.statusText}`);
}

export async function approveStaffAccess(staffId: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/access-request`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ status: 'approved' })
  });
  if (!res.ok) throw new Error(`Failed to approve staff: ${res.statusText}`);
}

export async function rejectStaffAccess(staffId: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/access-request`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ status: 'rejected' })
  });
  if (!res.ok) throw new Error(`Failed to reject staff: ${res.statusText}`);
}

export async function setPendingStaffAccess(staffId: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/access-request`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ status: 'pending' })
  });
  if (!res.ok) throw new Error(`Failed to set staff pending: ${res.statusText}`);
}

export async function updateStaffRole(staffId: string, restaurantRole: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/staff/${staffId}/role`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ restaurantRole })
  });
  if (!res.ok) throw new Error(`Failed to update staff role: ${res.statusText}`);
}
